import { useEffect, useState } from "react";
import { getSession, removeSession, setSession } from "../helpers/storage";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AMBASSADOR_ROLE,
  DWF_LEARNING_USERID,
  DWF_PAGEVIEW_COUNT,
  DWF_USER_ROLE,
} from "../helpers/constants";
import airtable from "../lib/airtables";
import { teacherFieldIds } from "../lib/airtables/tables/Teachers";
import { z } from "zod";
import { registerFormSchema } from "../lib/formSchemas";
import { hideLoading, showLoading } from "../helpers/loading";
import {
  loginLookup,
  fetchProxyToken,
  fetchGuestToken,
  storeProxyToken,
  storeEmail,
  clearProxyToken,
  getProxyToken,
  getStoredEmail,
  startTokenRefresh,
  startGuestTokenRefresh,
} from "../lib/proxyToken";

export const useAuthProvider = () => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [triedEmail, setTriedEmail] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const getTeacherValue = (record: any, fieldId: string) => record?.[fieldId];
  const isArtist = (record: any) =>
    getTeacherValue(record, teacherFieldIds.artist) === "Yes";

  useEffect(() => {
    const userId = getSession(DWF_LEARNING_USERID);
    const email = getStoredEmail();
    const existingToken = getProxyToken();

    // Anonymous browsing: configure the proxy with a read-only guest token.
    const startGuestSession = async () => {
      setUser(null);
      const token = await fetchGuestToken();
      if (token) {
        airtable.configureProxy(token);
        startGuestTokenRefresh((t: string) => airtable.configureProxy(t));
      }
      setLoading(false);
    };

    if (!userId || !email) {
      startGuestSession();
      return;
    }

    // Restore session: get a fresh token (or reuse existing), then fetch user
    const restoreSession = async () => {
      let token = existingToken;

      // Always fetch a fresh token on session restore to ensure validity
      const freshToken = await fetchProxyToken(userId, email);
      if (freshToken) {
        token = freshToken;
      }

      if (!token) {
        // Can't authenticate — clear the session and browse as a guest
        removeSession(DWF_LEARNING_USERID);
        removeSession(DWF_USER_ROLE);
        clearProxyToken();
        await startGuestSession();
        return;
      }

      airtable.configureProxy(token);
      startTokenRefresh(userId, email);

      const isAmbassador = getSession(DWF_USER_ROLE) === AMBASSADOR_ROLE;

      try {
        const res: any = isAmbassador
          ? await airtable.artistAmbassadors.select(userId)
          : await airtable.teachers.select(userId);
        if (isAmbassador && !res) throw new Error("Ambassador record not found");
        setUser(res);
        setLoading(false);
        if (
          location.pathname === "/" ||
          location.pathname.startsWith("/login")
        ) {
          navigate(isAmbassador ? "/ambassador" : "/app/dashboard", {
            replace: true,
          });
        }
      } catch {
        removeSession(DWF_LEARNING_USERID);
        removeSession(DWF_USER_ROLE);
        clearProxyToken();
        await startGuestSession();
      }
    };

    restoreSession();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getReturnUser = (user: any) => {
    const hasProfileName = !!getTeacherValue(user, teacherFieldIds.name);
    if (hasProfileName || isArtist(user)) {
      setUser(user);
      setTriedEmail("");
      setSession(DWF_LEARNING_USERID, user.id);
      removeSession(DWF_PAGEVIEW_COUNT);
      return { user };
    } else {
      setTriedEmail(getTeacherValue(user, teacherFieldIds.email));
      setUser(null);
      return { user, type: "register" };
    }
  };

  const loginUser = async (email: string) => {
    if (!email) return;

    showLoading("Verifying user...");

    // Call the server-side login function — handles Airtable lookup,
    // record creation, Zapier webhook, and JWT issuance.
    const result = await loginLookup(email);

    hideLoading();

    if (result.error) {
      return { error: result.error };
    }

    if (!result.user || !result.token) {
      return { error: "content" };
    }

    // Store token and configure the Airtable proxy
    storeProxyToken(result.token);
    storeEmail(email);
    airtable.configureProxy(result.token);
    startTokenRefresh(result.user.id, email);

    if (result.role === AMBASSADOR_ROLE) {
      setUser(result.user);
      setTriedEmail("");
      setSession(DWF_LEARNING_USERID, result.user.id);
      setSession(DWF_USER_ROLE, AMBASSADOR_ROLE);
      removeSession(DWF_PAGEVIEW_COUNT);
      return { user: result.user, type: "ambassador" };
    }

    return getReturnUser(result.user);
  };

  const registerUser = async (values: z.infer<typeof registerFormSchema>) => {
    // At this point the proxy is already configured from the login step
    let user = await airtable.teachers.getTeacherByEmail(values.email);
    if (!user?.id) return { error: "content" };

    const updatedUser = await airtable.teachers.update(user.id, {
      [teacherFieldIds.name]: `${values.firstName} ${values.lastName}`.trim(),
      [teacherFieldIds.newsletterSignup]: values.newsletter,
      [teacherFieldIds.firstName]: `${values.firstName}`,
      [teacherFieldIds.lastName]: `${values.lastName}`
    });

    if (!updatedUser) return { error: "content" };
    user = updatedUser;

    setUser(user);
    setTriedEmail("");
    setSession(DWF_LEARNING_USERID, user.id);
    removeSession(DWF_PAGEVIEW_COUNT);
    return { user: user };
  };

  const logout = () => {
    setUser(null);
    removeSession(DWF_LEARNING_USERID);
    removeSession(DWF_USER_ROLE);
    clearProxyToken();
    // Keep anonymous browsing working after sign-out
    fetchGuestToken().then((token) => {
      if (token) {
        airtable.configureProxy(token);
        startGuestTokenRefresh((t: string) => airtable.configureProxy(t));
      }
    });
    navigate("/");
  };

  return {
    loading,
    user,
    triedEmail,
    loginUser,
    registerUser,
    logout,
  };
};
